import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Injectable, InjectionToken, inject, input, effect, Input, Directive, booleanAttribute, ViewEncapsulation, ChangeDetectionStrategy, Component, NgModule } from '@angular/core';
import { isEmpty, isNotEmpty, uuid, hasClass, removeClass, addClass, createElement } from '@primeuix/utils';
import { SharedModule } from 'primeng/api';
import { BaseComponent, PARENT_INSTANCE } from 'primeng/basecomponent';
import * as i1 from 'primeng/bind';
import { Bind, BindModule } from 'primeng/bind';
import { style as style$1 } from '@primeuix/styles/badge';
import { BaseStyle } from 'primeng/base';
const style = /*css*/`
    ${style$1}

    /* For PrimeNG (directive)*/
    .p-overlay-badge {
        position: relative;
    }

    .p-overlay-badge > .p-badge {
        position: absolute;
        top: 0;
        inset-inline-end: 0;
        transform: translate(50%, -50%);
        transform-origin: 100% 0;
        margin: 0;
    }
`;
const classes = {
  root: ({
    instance
  }) => {
    const value = typeof instance.value === 'function' ? instance.value() : instance.value;
    const size = typeof instance.size === 'function' ? instance.size() : instance.size;
    const badgeSize = typeof instance.badgeSize === 'function' ? instance.badgeSize() : instance.badgeSize;
    const severity = typeof instance.severity === 'function' ? instance.severity() : instance.severity;
    return ['p-badge p-component', {
      'p-badge-circle': isNotEmpty(value) && String(value).length === 1,
      'p-badge-dot': isEmpty(value),
      'p-badge-sm': size === 'small' || badgeSize === 'small',
      'p-badge-lg': size === 'large' || badgeSize === 'large',
      'p-badge-xl': size === 'xlarge' || badgeSize === 'xlarge',
      'p-badge-info': severity === 'info',
      'p-badge-success': severity === 'success',
      'p-badge-warn': severity === 'warn',
      'p-badge-danger': severity === 'danger',
      'p-badge-secondary': severity === 'secondary',
      'p-badge-contrast': severity === 'contrast'
    }];
  }
};
let BadgeStyle = /*#__PURE__*/(() => {
  class BadgeStyle extends BaseStyle {
    name = 'badge';
    style = style;
    classes = classes;
    static ɵfac = /* @__PURE__ */(() => {
      let ɵBadgeStyle_BaseFactory;
      return function BadgeStyle_Factory(__ngFactoryType__) {
        return (ɵBadgeStyle_BaseFactory || (ɵBadgeStyle_BaseFactory = i0.ɵɵgetInheritedFactory(BadgeStyle)))(__ngFactoryType__ || BadgeStyle);
      };
    })();
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: BadgeStyle,
      factory: BadgeStyle.ɵfac
    });
  }
  return BadgeStyle;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
/**
 *
 * Badge represents people using icons, labels and images.
 *
 * [Live Demo](https://www.primeng.org/badge)
 *
 * @module badgestyle
 *
 */
var BadgeClasses = /*#__PURE__*/function (BadgeClasses) {
  /**
   * Class name of the root element
   */
  BadgeClasses["root"] = "p-badge";
  return BadgeClasses;
}(BadgeClasses || {});
const BADGE_INSTANCE = /*#__PURE__*/new InjectionToken('BADGE_INSTANCE');
const BADGE_DIRECTIVE_INSTANCE = /*#__PURE__*/new InjectionToken('BADGE_DIRECTIVE_INSTANCE');
/**
 * Badge Directive is directive usage of badge component.
 * @group Components
 */
let BadgeDirective = /*#__PURE__*/(() => {
  class BadgeDirective extends BaseComponent {
    $pcBadgeDirective = inject(BADGE_DIRECTIVE_INSTANCE, {
      optional: true,
      skipSelf: true
    }) ?? undefined;
    /**
     * Used to pass attributes to DOM elements inside the Badge component.
     * @defaultValue undefined
     * @deprecated use pBadgePT instead.
     * @group Props
     */
    ptBadgeDirective = input(...(ngDevMode ? [undefined, {
      debugName: "ptBadgeDirective"
    }] : []));
    /**
     * Used to pass attributes to DOM elements inside the Badge component.
     * @defaultValue undefined
     * @group Props
     */
    pBadgePT = input(...(ngDevMode ? [undefined, {
      debugName: "pBadgePT"
    }] : []));
    /**
     * Indicates whether the component should be rendered without styles.
     * @defaultValue undefined
     * @group Props
     */
    pBadgeUnstyled = input(...(ngDevMode ? [undefined, {
      debugName: "pBadgeUnstyled"
    }] : []));
    /**
     * When specified, disables the component.
     * @group Props
     */
    disabled;
    /**
     * Size of the badge, valid options are "large" and "xlarge".
     * @group Props
     */
    badgeSize;
    /**
     * Size of the badge, valid options are "large" and "xlarge".
     * @group Props
     * @deprecated use badgeSize instead.
     */
    set size(value) {
      this._size = value;
      console.log('size property is deprecated and will removed in v18, use badgeSize instead.');
    }
    get size() {
      return this._size;
    }
    _size;
    /**
     * Severity type of the badge.
     * @group Props
     */
    severity;
    /**
     * Value to display inside the badge.
     * @group Props
     */
    value;
    /**
     * Inline style of the element.
     * @group Props
     */
    badgeStyle;
    /**
     * Class of the element.
     * @group Props
     */
    badgeStyleClass;
    id;
    badgeEl;
    _componentStyle = inject(BadgeStyle);
    get activeElement() {
      return this.el.nativeElement.nodeName.indexOf('-') != -1 ? this.el.nativeElement.firstChild : this.el.nativeElement;
    }
    get canUpdateBadge() {
      return isNotEmpty(this.id) && !this.disabled;
    }
    constructor() {
      super();
      effect(() => {
        const pt = this.ptBadgeDirective() || this.pBadgePT();
        pt && this.directivePT.set(pt);
      });
      effect(() => {
        this.pBadgeUnstyled() && this.directiveUnstyled.set(this.pBadgeUnstyled());
      });
    }
    onChanges(changes) {
      const {
        value,
        size,
        severity,
        disabled,
        badgeStyle,
        badgeStyleClass
      } = changes;
      if (disabled) {
        this.toggleDisableState();
      }
      if (!this.canUpdateBadge) {
        return;
      }
      if (severity) {
        this.setSeverity(severity.previousValue);
      }
      if (size) {
        this.setSizeClasses();
      }
      if (value) {
        this.setValue();
      }
      if (badgeStyle || badgeStyleClass) {
        this.applyStyles();
      }
    }
    onAfterViewInit() {
      this.id = uuid('pn_id_') + '_badge';
      this.renderBadgeContent();
    }
    setValue(element) {
      const badge = element ?? this.document.getElementById(this.id);
      if (!badge) {
        return;
      }
      if (this.value != null) {
        if (hasClass(badge, 'p-badge-dot')) {
          removeClass(badge, 'p-badge-dot');
        }
        if (this.value && String(this.value).length === 1) {
          addClass(badge, 'p-badge-circle');
        } else {
          removeClass(badge, 'p-badge-circle');
        }
      } else {
        if (!hasClass(badge, 'p-badge-dot')) {
          addClass(badge, 'p-badge-dot');
        }
        removeClass(badge, 'p-badge-circle');
      }
      badge.textContent = '';
      const badgeValue = this.value != null ? String(this.value) : '';
      this.renderer.appendChild(badge, this.document.createTextNode(badgeValue));
    }
    setSizeClasses(element) {
      const badge = element ?? this.document.getElementById(this.id);
      if (!badge) {
        return;
      }
      if (this.badgeSize) {
        if (this.badgeSize === 'large') {
          addClass(badge, 'p-badge-lg');
          removeClass(badge, 'p-badge-xl');
        }
        if (this.badgeSize === 'xlarge') {
          addClass(badge, 'p-badge-xl');
          removeClass(badge, 'p-badge-lg');
        }
      } else if (this.size && !this.badgeSize) {
        if (this.size === 'large') {
          addClass(badge, 'p-badge-lg');
          removeClass(badge, 'p-badge-xl');
        }
        if (this.size === 'xlarge') {
          addClass(badge, 'p-badge-xl');
          removeClass(badge, 'p-badge-lg');
        }
      } else {
        removeClass(badge, 'p-badge-lg');
        removeClass(badge, 'p-badge-xl');
      }
    }
    renderBadgeContent() {
      if (this.disabled) {
        return;
      }
      const el = this.activeElement;
      const badge = createElement('span', {
        class: this.cx('root'),
        id: this.id,
        'p-bind': this.ptm('root')
      });
      this.setSeverity(null, badge);
      this.setSizeClasses(badge);
      this.setValue(badge);
      addClass(el, 'p-overlay-badge');
      this.renderer.appendChild(el, badge);
      this.badgeEl = badge;
      this.applyStyles();
    }
    applyStyles() {
      if (this.badgeEl && this.badgeStyle && typeof this.badgeStyle === 'object') {
        for (const [key, value] of Object.entries(this.badgeStyle)) {
          this.renderer.setStyle(this.badgeEl, key, value);
        }
      }
      if (this.badgeEl && this.badgeStyleClass) {
        this.badgeEl.classList.add(...this.badgeStyleClass.split(' '));
      }
    }
    setSeverity(oldSeverity, element) {
      const badge = element ?? this.document.getElementById(this.id);
      if (!badge) {
        return;
      }
      if (this.severity) {
        addClass(badge, `p-badge-${this.severity}`);
      }
      if (oldSeverity) {
        removeClass(badge, `p-badge-${oldSeverity}`);
      }
    }
    toggleDisableState() {
      if (!this.id) {
        return;
      }
      if (this.disabled) {
        const badge = this.activeElement?.querySelector(`#${this.id}`);
        if (badge) {
          this.renderer.removeChild(this.activeElement, badge);
        }
      } else {
        this.renderBadgeContent();
      }
    }
    static ɵfac = function BadgeDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || BadgeDirective)();
    };
    static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: BadgeDirective,
      selectors: [["", "pBadge", ""]],
      inputs: {
        ptBadgeDirective: [1, "ptBadgeDirective"],
        pBadgePT: [1, "pBadgePT"],
        pBadgeUnstyled: [1, "pBadgeUnstyled"],
        disabled: [0, "badgeDisabled", "disabled"],
        badgeSize: "badgeSize",
        size: "size",
        severity: "severity",
        value: "value",
        badgeStyle: "badgeStyle",
        badgeStyleClass: "badgeStyleClass"
      },
      features: [i0.ɵɵProvidersFeature([BadgeStyle, {
        provide: BADGE_DIRECTIVE_INSTANCE,
        useExisting: BadgeDirective
      }, {
        provide: PARENT_INSTANCE,
        useExisting: BadgeDirective
      }]), i0.ɵɵInheritDefinitionFeature]
    });
  }
  return BadgeDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
/**
 * Badge is a small status indicator for another element.
 * @group Components
 */
let Badge = /*#__PURE__*/(() => {
  class Badge extends BaseComponent {
    componentName = 'Badge';
    $pcBadge = inject(BADGE_INSTANCE, {
      optional: true,
      skipSelf: true
    }) ?? undefined;
    bindDirectiveInstance = inject(Bind, {
      self: true
    });
    onAfterViewChecked() {
      this.bindDirectiveInstance.setAttrs(this.ptms(['host', 'root']));
    }
    /**
     * Class of the element.
     * @deprecated since v20.0.0, use `class` instead.
     * @group Props
     */
    styleClass = input(...(ngDevMode ? [undefined, {
      debugName: "styleClass"
    }] : []));
    /**
     * Size of the badge, valid options are "large" and "xlarge".
     * @group Props
     */
    badgeSize = input(...(ngDevMode ? [undefined, {
      debugName: "badgeSize"
    }] : []));
    /**
     * Size of the badge, valid options are "large" and "xlarge".
     * @group Props
     */
    size = input(...(ngDevMode ? [undefined, {
      debugName: "size"
    }] : []));
    /**
     * Severity type of the badge.
     * @group Props
     */
    severity = input(...(ngDevMode ? [undefined, {
      debugName: "severity"
    }] : []));
    /**
     * Value to display inside the badge.
     * @group Props
     */
    value = input(...(ngDevMode ? [undefined, {
      debugName: "value"
    }] : []));
    /**
     * When specified, disables the component.
     * @group Props
     */
    badgeDisabled = input(false, {
      ...(ngDevMode ? {
        debugName: "badgeDisabled"
      } : {}),
      transform: booleanAttribute
    });
    _componentStyle = inject(BadgeStyle);
    get dataP() {
      return this.cn({
        circle: this.value() != null && String(this.value()).length === 1,
        empty: this.value() == null,
        disabled: this.badgeDisabled(),
        [this.severity()]: this.severity(),
        [this.size()]: this.size()
      });
    }
    static ɵfac = /* @__PURE__ */(() => {
      let ɵBadge_BaseFactory;
      return function Badge_Factory(__ngFactoryType__) {
        return (ɵBadge_BaseFactory || (ɵBadge_BaseFactory = i0.ɵɵgetInheritedFactory(Badge)))(__ngFactoryType__ || Badge);
      };
    })();
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: Badge,
      selectors: [["p-badge"]],
      hostVars: 5,
      hostBindings: function Badge_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵattribute("data-p", ctx.dataP);
          i0.ɵɵclassMap(ctx.cn(ctx.cx("root"), ctx.styleClass()));
          i0.ɵɵstyleProp("display", ctx.badgeDisabled() ? "none" : null);
        }
      },
      inputs: {
        styleClass: [1, "styleClass"],
        badgeSize: [1, "badgeSize"],
        size: [1, "size"],
        severity: [1, "severity"],
        value: [1, "value"],
        badgeDisabled: [1, "badgeDisabled"]
      },
      features: [i0.ɵɵProvidersFeature([BadgeStyle, {
        provide: BADGE_INSTANCE,
        useExisting: Badge
      }, {
        provide: PARENT_INSTANCE,
        useExisting: Badge
      }]), i0.ɵɵHostDirectivesFeature([i1.Bind]), i0.ɵɵInheritDefinitionFeature],
      decls: 1,
      vars: 1,
      template: function Badge_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵtext(0);
        }
        if (rf & 2) {
          i0.ɵɵtextInterpolate(ctx.value());
        }
      },
      dependencies: [CommonModule, SharedModule, BindModule],
      encapsulation: 2,
      changeDetection: 0
    });
  }
  return Badge;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let BadgeModule = /*#__PURE__*/(() => {
  class BadgeModule {
    static ɵfac = function BadgeModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || BadgeModule)();
    };
    static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: BadgeModule
    });
    static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      imports: [Badge, SharedModule, SharedModule]
    });
  }
  return BadgeModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { Badge, BadgeClasses, BadgeDirective, BadgeModule, BadgeStyle };
